import { CheckoutSession } from '@nemuru-eng/js-api-client';
import { useEffect } from 'react';

import { usePaymentMethods } from '@/components/modules/Application/components/PaymentMethod/_shared/hooks/usePaymentMethods';
import { EditConfirmAlert } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/_shared/components/EditConfirmAlert';
import { EditLeaveAlert } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/_shared/components/EditLeaveAlert';
import { useCheckoutSessionContext } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/_shared/contexts/CheckoutSessionContext';
import { useSubmitDialog } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/_shared/hooks/useSubmitDialog';
import { EditCheckoutSessionCartSteps } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/Header/CheckoutSessionActions/components/EditCheckoutSessionCartDialog/components/EditCheckoutSessionCartSteps';
import { useEditCart } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/Header/CheckoutSessionActions/components/EditCheckoutSessionCartDialog/hooks/useEditCart';
import { useRefreshAllPaymentMethodDetailEntities } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/Header/CheckoutSessionActions/components/EditCheckoutSessionCartDialog/hooks/useRefreshAllPaymentMethodDetailEntities';
import { useRestoreDataFromCheckoutSession } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/Header/CheckoutSessionActions/components/EditCheckoutSessionCartDialog/hooks/useRestoreDataFromCheckoutSession';
import { NemuruDialog } from '@/components/shared/components/NemuruDialog';

interface EditCheckoutSessionCartDialogProps {
  checkoutSession: CheckoutSession;
  opened: boolean;
  setOpened: (opened: boolean) => void;
}

export const EditCheckoutSessionCartDialog = ({
  checkoutSession,
  opened,
  setOpened,
}: EditCheckoutSessionCartDialogProps) => {
  const { paymentMethodDetailEntities } = useCheckoutSessionContext();
  const {
    openedLeaveAlert,
    openedConfirmAlert,
    setOpenedLeaveAlert,
    setOpenedConfirmAlert,
    handleSubmit,
    handleClose,
  } = useSubmitDialog({ setOpened });
  const {
    restore,
    loading: loadingRestore,
    createPaymentMethodsEntitiesFromCheckoutSession,
    reset,
  } = useRestoreDataFromCheckoutSession(checkoutSession);
  const { refreshAllPaymentMethodDetailEntities } = useRefreshAllPaymentMethodDetailEntities();
  const { cart, editCart, loading: loadingEditCart } = useEditCart(checkoutSession);
  const { paymentMethodEntities } = usePaymentMethods();

  useEffect(() => {
    if (opened) {
      restore(true);
    } else {
      reset();
    }
  }, [opened]);

  useEffect(() => {
    if (opened && cart && !paymentMethodEntities) {
      // This will allow validation of cart total according to agent's available payment methods
      createPaymentMethodsEntitiesFromCheckoutSession(checkoutSession);
    }
  }, [cart, paymentMethodEntities]);

  useEffect(() => {
    if (!paymentMethodEntities) return;

    // Refresh async all payment method detail entities on CheckoutSessionContext
    // in order to display its conditions in the CONFIRM_NEW_AVAILABLE_PAYMENT_METHODS step
    refreshAllPaymentMethodDetailEntities(paymentMethodDetailEntities);
  }, [paymentMethodDetailEntities]);

  const loading = loadingRestore || !cart;

  return (
    <NemuruDialog
      opened={opened}
      setOpened={setOpened}
      hideButtonsWrapper
      dialogProps={{
        maxWidth: 'md',
        ...(loading ? { PaperProps: { style: { minHeight: '525px' } } } : {}),
      }}
      loading={loading}
      onClose={() => setOpenedLeaveAlert(true)}
    >
      <EditCheckoutSessionCartSteps
        checkoutSession={checkoutSession}
        onComplete={() => setOpenedConfirmAlert(true)}
        onCancel={() => setOpenedLeaveAlert(true)}
      />
      <EditLeaveAlert opened={openedLeaveAlert} setOpened={setOpenedLeaveAlert} onSubmit={handleClose} />
      <EditConfirmAlert
        opened={openedConfirmAlert}
        setOpened={setOpenedConfirmAlert}
        onSubmit={() => handleSubmit(editCart)}
        loading={loadingEditCart}
      />
    </NemuruDialog>
  );
};
